import type { FC } from 'react'
import classNames from 'classnames'
import { Asterisk } from './Asterisk'

export const FormLabel: FC<{
  required?: boolean
  id?: string
  label?: string
  labelClass?: string
  /**
   * The id of the form field that this label is associated with.
   * If the form field is not associated with a form, use the `HasNoAssociatedFormField` symbol.
   */
  for: string | typeof HasNoAssociatedFormField
  variant?: 'default' | 'compact'
}> = ({ label, labelClass, required, for: forId, variant = 'default', id }) => {
  const classes = 'text-body-medium w-full text-on-surface-var '

  if (!label) return null

  const htmlFor = typeof forId === 'symbol' ? undefined : forId

  return (
    <label
      id={id}
      htmlFor={htmlFor}
      className={classNames(classes, labelClass, {
        'px-4': variant === 'default',
        'px-3': variant === 'compact',
      })}
      data-testid={`${htmlFor}-field-label`}
    >
      {label}
      {required && (
        <Asterisk
          ariaHidden={
            // Since this asterisk is inside the label, it should be invisible to
            // screen readers, because the label is associated with the form field
            // via the `for` prop, and the input will have the required attribute.
            true
          }
        />
      )}
    </label>
  )
}

// For accessibility reasons, I wanted to make the `for` prop non-optional. So
// this symbol is a slightly more explicit way of opting out of providing that
// prop.
// oxlint-disable-next-line react/only-export-components -- pre-existing: eslint-plugin-react-refresh 0.4 did not report this
export const HasNoAssociatedFormField = Symbol('HasNoAssociatedFormField')
